import {
  AggregateFunctionName,
  NodeAggregatedResults,
  RunDto,
  RunResultsHistoric,
  RunResultsHistoricAggregate,
} from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import {
  getNodeUnitByLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import {
  Aggregation,
  NodeResultRow,
} from "@features/results/Result/ChartsContainer/NodeResultsTable"
import {
  HistoricData,
  HistoricLineOptions,
} from "@features/results/Result/ChartsContainer/TimeSeriesChart"
import { getNodeResults } from "@features/results/Result/runResult.utils"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import logger from "@utils/logger"
import { extractScenarioNameFromRun } from "@utils/run"
import { Chart, Plugin } from "chart.js"
import { quantileSorted } from "d3"

export const plugins: Plugin[] = [
  {
    id: "historic",
    beforeDraw: (chart: Chart) => {
      if (!chart.options.plugins || !chart.data.labels || !chart.scales.x) return
      const options = (chart.options as HistoricLineOptions).plugins.historic
      if (options.show) {
        const ctx = chart.ctx
        const xAxis = chart.scales.x
        const xValue = xAxis.getPixelForValue(chart.data.labels.length - options.right)
        ctx.save()
        ctx.strokeStyle = "#D6002A"
        ctx.lineWidth = 1
        ctx.beginPath()
        const legendHeight = chart.legend?.height ?? 0
        ctx.moveTo(xValue, legendHeight)
        ctx.lineTo(xValue, chart.height - xAxis.height)
        ctx.stroke()
        ctx.restore()

        ctx.fillStyle = "rgba(52, 52, 52, 0.05)"
        const xLeftMargin = Array.from(Object.values(chart.scales)).filter(
          (s) => s.position === "left",
        )
        const xLeft = xLeftMargin[0]?.width || 0
        ctx.fillRect(
          xLeft,
          legendHeight,
          xValue - xLeft,
          chart.height - xAxis.height - legendHeight,
        )
        ctx.restore()
      }
    },
  },
]

export const getHistoricValues = (
  allRunsNodeResultsWithHistoricData: RunResultsHistoric | undefined,
): { data: HistoricData[]; index: number } => {
  if (
    !allRunsNodeResultsWithHistoricData ||
    Object.values(allRunsNodeResultsWithHistoricData).length === 0
  ) {
    return { data: [], index: 0 }
  }
  const data: HistoricData[] = []
  let index = 0
  let maxHistoricSize = -1

  // we need to fill all missing timesteps
  // so we need to go through each node result historic data in each run
  // to first identify all the timesteps
  const seenTimesteps = new Set<string>()
  Object.entries(allRunsNodeResultsWithHistoricData).forEach(
    ([_, thisRunNodesResultsWithHistoric]) => {
      Object.values(thisRunNodesResultsWithHistoric).map((nodeResultWithHistoric) => {
        const timeStepsInHistoricData = nodeResultWithHistoric.historic?.map((y) => y["Date"])
        timeStepsInHistoricData?.forEach((item) => seenTimesteps.add(item))
      })
    },
  )

  Object.entries(allRunsNodeResultsWithHistoricData).forEach(
    ([_, thisRunNodesResultsWithHistoric]) => {
      Object.entries(thisRunNodesResultsWithHistoric).forEach(
        ([_, { names, timesteps, historic }], i) => {
          if (!historic) {
            data.push({})
            return
          }

          // find first non lag name with timestep and use this timestep as end point of historic data
          const nameEntries = Object.entries(names)
          const nameValues = Object.values(names)
          // assume that last name is not lag name
          const lastName = nameValues[nameValues.length - 1]
          let firstTimestep = nameEntries[0]?.[0]
          for (const entry of nameEntries) {
            if (entry[1] === lastName) {
              firstTimestep = entry[0]
              break
            }
          }

          // if there is historic data, we need to fill the series with potentially missing dates
          const newHistoric = JSON.parse(JSON.stringify(historic))
          const historicSet = new Set(historic.map((x) => x["Date"]))
          if (historicSet.size > 0) {
            const missingData = seenTimesteps.difference(historicSet)
            missingData.forEach((x) => {
              newHistoric.push({ Date: x, Mnem: "", Value: NaN })
            })
          }

          // TODO ms - since we're now pushing missing dates for all timesteps returning index from getHistoricValues is not needed we can just return results
          // sort dates after pushing missing timesteps - 1 : -1 because of chrome
          newHistoric.sort((a: { Date: string }, b: { Date: string }) =>
            parseInt(a.Date) > parseInt(b.Date) ? 1 : -1,
          )

          // we get the longest historic data to use
          // this is used to set index.
          const singleData: HistoricData = {}
          if (maxHistoricSize < newHistoric.length) {
            index = i
            maxHistoricSize = newHistoric.length
          }

          for (const h of newHistoric) {
            // we stop adding data when we reach the start date of the sampling data
            if (h.Date === firstTimestep) break
            // TODO ms: uncomment if we want to filter 0 values
            // if (parseFloat(h.Value) === 0) continue
            singleData[h.Date] = parseFloat(h.Value)
          }
          data.push(singleData)
        },
      )
    },
  )
  // fill empty values so charts are aligned to the right
  const longestTimesteps = Object.keys(data[index] ?? {})
  if (longestTimesteps.length === 0) return { data, index }
  data.forEach((value, i) => {
    if (i === index) return
    longestTimesteps.forEach((t) => {
      if (!(t in value)) value[t] = NaN
    })
  })
  return { data, index }
}

export const getTicks = (format: string, axisIndex: number, totalNumberOfAxis: number) => {
  const finalFormat = format || "{,.3f}"
  let color = "black"
  if (totalNumberOfAxis > 1 && color_tones[axisIndex]) {
    color = to_rgba(lighter_rgba(color_tones[axisIndex], -10, 1.0))
  }

  return {
    callback: (value: number | string) => formatValuePythonic(value, finalFormat),
    color,
  }
}

export const getAllTimestepsFromRows = (rows: NodeResultRow[]) => {
  const timesteps = new Set<string>()

  rows.forEach((row) => {
    row.scenarios.forEach((scenarioGroup) => {
      scenarioGroup.nodes.forEach((nodeResult) => {
        nodeResult.aggregations.forEach((agg) => {
          Object.keys(agg.timestepValues).forEach((timestep) => timesteps.add(timestep))
        })
      })
    })
  })

  return Array.from(timesteps)
}

export const getNodeTableRowsFromAggregate = (
  aggregateData: RunResultsHistoricAggregate,
  allRunsNodeResultsWithHistoricData: RunResultsHistoric | undefined,
  runs: RunDto[],
  nodes: GroupNodeLabel[],
  lagShift: number,
  percentiles: number[],
  showMean = false,
  showMeanLine = false,
  showHistoric = false,
  historicValues: HistoricData[] = [],
): NodeResultRow[] => {
  const rows: NodeResultRow[] = []
  const historicEmptyValues: HistoricData = {}
  let shouldShowHistoric = false
  if (showHistoric && historicValues.length > 0) {
    const longestHistoric = historicValues.reduce((longest, current) =>
      Object.keys(current).length > Object.keys(longest).length ? current : longest,
    )
    Object.keys(longestHistoric).forEach((key) => {
      historicEmptyValues[key] = NaN
    })
    shouldShowHistoric = Object.keys(historicEmptyValues).length > 0
  }
  let dataIndex = 0
  Object.entries(aggregateData.results).forEach(([runId, runValues]) => {
    const run = runs.find((run) => run.key === runId)
    if (!run) return
    const scenarioName = extractScenarioNameFromRun(run)
    const runNameWithoutScenario = (run.run_name || "").replace(`_${scenarioName}`, "")

    let row = rows.find(({ runName }) => runName === runNameWithoutScenario)
    if (!row) {
      row = { runName: runNameWithoutScenario, scenarios: [] }
      rows.push(row)
    }

    Object.entries(runValues).forEach(([nodeKey, nodeValues]) => {
      const historic = shouldShowHistoric ? historicValues[dataIndex] : undefined
      const historicFilled =
        shouldShowHistoric && Object.keys(historic ?? {}).length > 0
          ? historic
          : historicEmptyValues
      dataIndex += 1
      const node: GroupNodeLabel | undefined = nodes.find(
        (n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey,
      )
      const unitFormat = nodeValues.unitFormat || getNodeUnitByLabel(nodeKey)
      if (!node) return
      const aggregations: Aggregation[] = []

      if (showMean) {
        const mean: Record<string, number> = {}
        const dev1_low: Record<string, number> = {}
        const dev2_low: Record<string, number> = {}
        const dev3_low: Record<string, number> = {}
        const dev1_up: Record<string, number> = {}
        const dev2_up: Record<string, number> = {}
        const dev3_up: Record<string, number> = {}
        Object.entries(nodeValues.timesteps).forEach(([timestep, value], index) => {
          if (index < lagShift) return
          mean[timestep] = value.mean
          dev1_low[timestep] = value.mean - value.d3_deviation
          dev2_low[timestep] = value.mean - 2 * value.d3_deviation
          dev3_low[timestep] = value.mean - 3 * value.d3_deviation
          dev1_up[timestep] = value.mean + value.d3_deviation
          dev2_up[timestep] = value.mean + 2 * value.d3_deviation
          dev3_up[timestep] = value.mean + 3 * value.d3_deviation
        })
        if (shouldShowHistoric) {
          if (showMeanLine) {
            Object.assign(mean, historicFilled)
          }
          Object.assign(dev1_low, historicEmptyValues)
          Object.assign(dev2_low, historicEmptyValues)
          Object.assign(dev3_low, historicEmptyValues)
          Object.assign(dev1_up, historicEmptyValues)
          Object.assign(dev2_up, historicEmptyValues)
          Object.assign(dev3_up, historicEmptyValues)
        }

        aggregations.push(
          ...[
            { name: "μ+3σ", timestepValues: dev3_up },
            { name: "μ+2σ", timestepValues: dev2_up },
            { name: "μ+σ", timestepValues: dev1_up },
            ...(showMeanLine ? [{ name: "μ", timestepValues: mean }] : []),
            { name: "μ-σ", timestepValues: dev1_low },
            { name: "μ-2σ", timestepValues: dev2_low },
            { name: "μ-3σ", timestepValues: dev3_low },
          ],
        )
      } else {
        const medianValues: Record<string, number> = {}
        const percentileValues = new Map<number, Record<string, number>>()
        const meanValues: Record<string, number> = {}
        Object.entries(nodeValues.timesteps).forEach(([timestep, values], index) => {
          if (index < lagShift) return
          const rawResults = getNodeResults(allRunsNodeResultsWithHistoricData, runId, nodeKey)
            ?.timesteps?.[timestep]

          percentiles.forEach((percentile) => {
            const valuesByPercentile = percentileValues.get(percentile) ?? {}
            valuesByPercentile[timestep] = getQuantileValue(percentile, values, rawResults ?? [])
            percentileValues.set(percentile, valuesByPercentile)
          })
          medianValues[timestep] = values.median
          meanValues[timestep] = values.mean
        })

        if (shouldShowHistoric) {
          percentileValues.forEach((valuesByPercentile) => {
            Object.assign(valuesByPercentile, historicEmptyValues)
          })
          Object.assign(medianValues, historicFilled)
          if (showMeanLine) {
            Object.assign(meanValues, historicEmptyValues)
          }
        }

        if (showMeanLine) {
          aggregations.push({
            name: "Mean",
            timestepValues: meanValues,
          })
        }

        aggregations.push({
          name: "Median",
          timestepValues: medianValues,
        })

        percentiles.forEach((percentile) => {
          aggregations.push({
            name: `${percentile}th percentile`,
            timestepValues: percentileValues.get(percentile) ?? {},
          })
        })
      }

      // Find or create scenario group
      let scenarioGroup = row.scenarios.find((s) => s.name === scenarioName)
      if (!scenarioGroup) {
        scenarioGroup = { name: scenarioName, nodes: [] }
        row.scenarios.push(scenarioGroup)
      }

      // Add node to scenario group
      scenarioGroup.nodes.push({
        node: node.label,
        unitFormat,
        aggregations,
      })
    })
  })
  return rows
}

export const getQuantileValue = (
  percentile: number,
  nodeAggregatedResults: NodeAggregatedResults,
  nodeRawResults: string[] | number[],
): number => {
  const quantileKey = `quantile_${percentile}th` as AggregateFunctionName
  if (quantileKey in nodeAggregatedResults) {
    return nodeAggregatedResults[quantileKey as keyof NodeAggregatedResults]
  }

  return calculateQuantile(percentile, nodeRawResults)
}

const calculateQuantile = (percentile: number, values: number[] | string[]): number => {
  if (values.find((value) => typeof value === "string")) {
    logger.error(
      `Could not determine quantile for ${percentile} percentile as provided data is not numeric`,
      values,
    )
    return NaN
  }

  const sortedValues = (values as number[]).toSorted((valueA, valueB) => valueA - valueB)

  const firstValue = sortedValues[0]
  if (firstValue === undefined) {
    logger.error(
      `Could not determine quantile for ${percentile} percentile as provided data is empty`,
    )
    return NaN
  }

  if (sortedValues.length < 2) return firstValue

  const quantile = quantileSorted(sortedValues, percentile / 100)
  if (!quantile) {
    logger.error(
      `Something went wrong. Could not determine quantile for ${percentile} percentile`,
      values,
    )
    return NaN
  }

  return quantile
}
